<template>
  <div class="ngc-card" :style="teamAccentStyle">
    <!-- Top row: context + status -->
    <div class="ngc-top-row">
      <span v-if="game.importanceLabel || game.roundLabel" class="ngc-context-badge">
        <i v-if="game.postseason" class="fa fa-trophy" aria-hidden="true"></i>
        {{ game.importanceLabel || game.roundLabel }}
      </span>
      <span class="ngc-status-badge" :class="statusClass">
        <span v-if="isLive" class="ngc-live-dot"></span>
        {{ game.statusDisplay || game.statusType }}
      </span>
    </div>

    <!-- Compact score area -->
    <div class="ngc-score-stack">
      <!-- Away row -->
      <div class="ngc-team-row" :class="{ 'ngc-team-row--winner': isWinner('away'), 'ngc-team-row--loser': isFinal && !isWinner('away') }">
        <div class="ngc-team-main">
        <img
          v-if="game.awayTeam?.logoUrl"
          :src="game.awayTeam.logoUrl"
          :alt="`${game.awayTeam.fullName || game.awayTeam.name} logo`"
          class="ngc-logo"
            width="28"
            height="28"
        />
          <span v-else class="ngc-logo-fallback" aria-hidden="true">
            {{ game.awayTeam?.espnAbbreviation?.slice(0, 1) || 'A' }}
          </span>
          <div class="ngc-team-details">
            <div class="ngc-team-name-row">
              <span class="ngc-team-name">{{ game.awayTeam?.name || 'Away' }}</span>
              <span v-if="isFinal && isWinner('away')" class="ngc-win-mark" aria-label="Winner">✓</span>
            </div>
          <span v-if="game.awayTeam?.record" class="ngc-record">{{ game.awayTeam.record }}</span>
          </div>
        </div>
        <div v-if="isFinal || isLive" class="ngc-score" :class="{ 'ngc-score--winner': isWinner('away') }">
          {{ game.score?.awayScore ?? '–' }}
        </div>
      </div>

      <!-- Home row -->
      <div class="ngc-team-row" :class="{ 'ngc-team-row--winner': isWinner('home'), 'ngc-team-row--loser': isFinal && !isWinner('home') }">
        <div class="ngc-team-main">
        <img
          v-if="game.homeTeam?.logoUrl"
          :src="game.homeTeam.logoUrl"
          :alt="`${game.homeTeam.fullName || game.homeTeam.name} logo`"
          class="ngc-logo"
            width="28"
            height="28"
        />
          <span v-else class="ngc-logo-fallback" aria-hidden="true">
            {{ game.homeTeam?.espnAbbreviation?.slice(0, 1) || 'H' }}
          </span>
          <div class="ngc-team-details">
            <div class="ngc-team-name-row">
              <span class="ngc-team-name">{{ game.homeTeam?.name || 'Home' }}</span>
              <span v-if="isFinal && isWinner('home')" class="ngc-win-mark" aria-label="Winner">✓</span>
            </div>
            <span v-if="game.homeTeam?.record" class="ngc-record">{{ game.homeTeam.record }}</span>
          </div>
        </div>
        <div v-if="isFinal || isLive" class="ngc-score" :class="{ 'ngc-score--winner': isWinner('home') }">
          {{ game.score?.homeScore ?? '–' }}
        </div>
      </div>
    </div>

    <div class="ngc-game-meta">
      <span v-if="isPre" class="ngc-tipoff">
        <i class="fa fa-clock" aria-hidden="true"></i> {{ tipoffDisplay }}
      </span>
      <span v-else-if="isLive" class="ngc-clock">
        <i class="fa fa-bolt" aria-hidden="true"></i> {{ clockDisplay }}
      </span>
      <span v-else-if="game.score?.margin === 1" class="ngc-margin-badge">1-pt finish</span>
      <span v-else-if="game.score?.overtime" class="ngc-margin-badge ngc-margin-badge--ot">OT</span>
      <span v-if="game.broadcast" class="ngc-broadcast">
        <i class="fa fa-tv" aria-hidden="true"></i> {{ game.broadcast }}
      </span>
      <span v-if="isPre && game.odds?.spread" class="ngc-spread">Spread: {{ game.odds.spread }}</span>
    </div>

    <!-- Series strip (playoffs only) -->
    <div v-if="game.seriesSummary" class="ngc-series-strip">
      <i class="fa fa-chart-line" aria-hidden="true"></i>
      {{ game.seriesSummary }}
    </div>

    <!-- Intelligence preview -->
    <div
      v-if="game.gameSummary && (isFinal || isLive)"
      class="ngc-narrative"
      :class="{ 'ngc-narrative--live': isLive }"
    >
      <i :class="isLive ? 'fa fa-bolt' : 'fa fa-lightbulb'" aria-hidden="true"></i>
      {{ game.gameSummary }}
    </div>

    <!-- Live / final intel pills -->
    <div v-if="showEnrichment" class="ngc-enrichment">
      <span v-if="leadPerformer" class="ngc-pill ngc-pill--player">
        <i class="fa fa-star" aria-hidden="true"></i>
        {{ leadPerformer.playerName }}
        <strong>{{ leadPerformer.statLine }}</strong>
      </span>
      <span
        v-for="(stat, i) in previewStats"
        :key="i"
        class="ngc-pill ngc-pill--stat"
      >
        {{ stat.description || stat.label }}
      </span>
    </div>

    <!-- CTAs -->
    <div v-if="(isFinal || isLive) && gameLink" class="ngc-ctas">
      <router-link
        class="ngc-btn ngc-btn--primary"
        :class="{ 'ngc-btn--live': isLive }"
        :to="gameLink"
      >
        <i class="fa fa-table-list" aria-hidden="true"></i>
        {{ isLive ? 'View Live Box Score' : 'View Box Score' }}
      </router-link>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NbaGameCard',
  props: {
    game: {
      type: Object,
      required: true,
    },
  },
  computed: {
    statusType() {
      return (this.game?.statusType || '').toUpperCase();
    },
    isFinal() { return this.statusType === 'FINAL'; },
    isLive() { return this.statusType === 'IN' || this.statusType === 'LIVE'; },
    isPre() { return !this.isFinal && !this.isLive; },
    statusClass() {
      if (this.isFinal) return 'status--final';
      if (this.isLive) return 'status--live';
      return 'status--pre';
    },
    awayAbbr() {
      return this.game?.awayTeam?.espnAbbreviation || '';
    },
    homeAbbr() {
      return this.game?.homeTeam?.espnAbbreviation || '';
    },
    gameLink() {
      return this.game?.boxScoreUrl || null;
    },
    teamAccentStyle() {
      const away = this.normalizeColor(this.game?.awayTeam?.primaryColor);
      const home = this.normalizeColor(this.game?.homeTeam?.primaryColor);
      const winner = this.game?.score?.winner === this.homeAbbr ? home : away;
      return {
        '--away-accent': away,
        '--home-accent': home,
        '--winner-accent': winner,
      };
    },
    tipoffDisplay() {
      const dt = this.game?.gameDateTime;
      if (!dt) return 'TBD';
      try {
        return new Date(dt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
      } catch {
        return 'TBD';
      }
    },
    clockDisplay() {
      const p = this.game?.period;
      const c = this.game?.clock;
      if (!p) return 'Live';
      const label = p <= 4 ? `Q${p}` : `OT${p - 4}`;
      return c ? `${label} ${c}` : label;
    },
    leadPerformer() {
      const list = Array.isArray(this.game?.topPerformers) ? this.game.topPerformers : [];
      return list[0] || null;
    },
    previewStats() {
      const list = Array.isArray(this.game?.notableStats) ? this.game.notableStats : [];
      return list.slice(0, 2);
    },
    showEnrichment() {
      return (this.isFinal || this.isLive) && (this.leadPerformer || this.previewStats.length);
    },
  },
  methods: {
    isWinner(side) {
      if (!this.isFinal || !this.game?.score?.winner) return false;
      const winner = this.game.score.winner;
      if (side === 'away') return winner === this.game.awayTeam?.espnAbbreviation;
      if (side === 'home') return winner === this.game.homeTeam?.espnAbbreviation;
      return false;
    },
    normalizeColor(value) {
      const fallback = '#3b82f6';
      if (!value) return fallback;
      const clean = String(value).trim().replace('#', '');
      return /^[0-9a-fA-F]{6}$/.test(clean) ? `#${clean}` : fallback;
    },
  },
};
</script>

<style scoped>
.ngc-card {
  background: #ffffff;
  border: 1px solid #dbeafe;
  border-top: 2px solid var(--winner-accent, #0b7ee3);
  border-radius: 14px;
  padding: 12px;
  font-family: 'Titillium Web', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ngc-card:hover { border-color: #93c5fd; box-shadow: 0 4px 14px rgba(11, 126, 227, 0.1); }

/* Top row */
.ngc-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.ngc-context-badge {
  background: rgba(251, 191, 36, 0.12);
  color: #b45309;
  border: 1px solid rgba(251, 191, 36, 0.35);
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
}
.ngc-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
}
.status--final { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }
.status--live { background: #dc2626; color: #fff; animation: pulse-badge 2s infinite; }
.status--pre { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }

@keyframes pulse-badge {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.ngc-live-dot {
  width: 5px; height: 5px;
  background: #fff;
  border-radius: 50%;
  animation: blink-dot 1.2s infinite;
}
@keyframes blink-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.1; }
}

/* Score stack */
.ngc-score-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ngc-team-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px;
  background: #ffffff;
  transition: opacity 0.2s;
}

.ngc-team-row--winner {
  border-color: color-mix(in srgb, var(--winner-accent, #0b7ee3) 35%, #dbeafe);
  box-shadow: inset 3px 0 0 color-mix(in srgb, var(--winner-accent, #0b7ee3) 65%, #ffffff);
}

.ngc-team-row--loser { opacity: 0.56; }
.ngc-score--winner { color: #111827; }

.ngc-team-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.ngc-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
}
.ngc-logo-fallback {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
}

.ngc-team-details {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.ngc-team-name-row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.ngc-team-name {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ngc-record {
  font-size: 10px;
  color: #64748b;
}

.ngc-win-mark {
  color: #059669;
  font-size: 11px;
  font-weight: 900;
  flex-shrink: 0;
}

.ngc-score {
  font-size: 24px;
  font-weight: 900;
  color: #1f2937;
  font-variant-numeric: tabular-nums;
  min-width: 36px;
  text-align: right;
  flex-shrink: 0;
  line-height: 1;
}

.ngc-game-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 11px;
  color: #6b7280;
}
.ngc-tipoff {
  font-size: 11px;
  font-weight: 600;
  color: #1d4ed8;
}
.ngc-clock {
  font-size: 11px;
  font-weight: 700;
  color: #dc2626;
}
.ngc-broadcast,
.ngc-spread {
  font-size: 11px;
  color: #64748b;
}
.ngc-margin-badge {
  background: rgba(251, 191, 36, 0.12);
  color: #b45309;
  border: 1px solid rgba(251, 191, 36, 0.25);
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 8px;
  text-transform: uppercase;
}
.ngc-margin-badge--ot {
  background: rgba(139, 92, 246, 0.12);
  color: #7c3aed;
  border-color: rgba(139, 92, 246, 0.25);
}

/* Series strip */
.ngc-series-strip {
  background: rgba(251, 191, 36, 0.07);
  border: 1px solid rgba(251, 191, 36, 0.18);
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 700;
  color: #b45309;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Narrative */
.ngc-narrative {
  font-size: 12px;
  color: #4b5563;
  line-height: 1.45;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.ngc-narrative i { color: #0b7ee3; margin-top: 2px; flex-shrink: 0; }
.ngc-narrative--live {
  background: rgba(220, 38, 38, 0.06);
  border: 1px solid rgba(220, 38, 38, 0.15);
  border-radius: 8px;
  padding: 8px 10px;
}
.ngc-narrative--live i { color: #dc2626; }

.ngc-enrichment {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.ngc-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #374151;
  background: #f0f7ff;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  padding: 3px 8px;
  line-height: 1.3;
  max-width: 100%;
}
.ngc-pill strong {
  font-weight: 800;
  color: #1d4ed8;
}
.ngc-pill--player i {
  color: #f59e0b;
  font-size: 9px;
}
.ngc-pill--stat {
  background: #fffbeb;
  border-color: #fde68a;
  color: #78350f;
}

/* CTAs */
.ngc-ctas {
  display: block;
  margin-top: 2px;
}
.ngc-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: transform 0.12s, opacity 0.12s;
  text-decoration: none;
}
.ngc-btn:hover { transform: translateY(-1px); opacity: 0.9; }
.ngc-btn:focus-visible { outline: 2px solid #0b7ee3; outline-offset: 2px; }
.ngc-btn--primary { background: #1d4ed8; color: #fff; }
.ngc-btn--live { background: #dc2626; }
.ngc-btn--live:hover { background: #b91c1c; }
</style>
